import React, { useContext } from 'react';

import TM from 'ts-translate-js';
import { useIFrameResize } from '../../../logic/hooks/utils';
import { SelectedTicketLabelView } from './selected-ticket-label-view';
import { AddToCartItem, AddToCartItemType, AddToCartPackageItem, AddToCartTicketItem, AddToCartTicketPackItem, getTicketIdsFromItems } from '../../../stores/add-to-cart-stores/models/add-to-cart-items';
import { useAddTicketsToCartStore } from '../../../stores';
import { SiteCustomizationContext } from '../../../logic/reactcontext/site-customization-context';
import { Warning } from '../../../logic/models/ticketselectionwarning';
import { uniq } from 'lodash';

const tr = TM.boundTR;
const accounting = require('accounting');

export interface SelectedItemLabelProps {
    item: AddToCartItem;
    ticketInfosLoading?: boolean;
    onTicketLabelClick: (itemId: string) => void;
    onSelectedTicketsRemoved: (ticketIds: number[]) => void;
}

export function SelectedItemLabel({item, ticketInfosLoading, onTicketLabelClick, onSelectedTicketsRemoved}: SelectedItemLabelProps) {
    useIFrameResize();
    const warningsByItemId = useAddTicketsToCartStore(s => s.warningsByItemId);
    const warnings = warningsByItemId[item._id] || [];

    const { siteSettings } = useContext(SiteCustomizationContext);

    let mainText = "";
    let rightText = "";
    let otherInfo = "";
    let handleOnLabelClick: () => void = null;
    let handleOnRemoveClick: () => void = null;

    let selectedTicketLabels = [];

    const createSelectedTicketLabelView = (key: string, mainText: string, otherInfo: string, rightText: string, loading: boolean, onClick: () => void, onRemoveClick: () => void, warnings: Warning[]) => {
        return <SelectedTicketLabelView 
            key={key}
            mainText={mainText}
            otherInfo={otherInfo}
            rightText={rightText}
            loading={loading}
            onClick={onClick}
            onRemoveClick={onRemoveClick}
            warnings={warnings}
        />;
    };

    if(item.type === AddToCartItemType.Ticket) {
        const ticketItem = item as AddToCartTicketItem;

        mainText = ticketItem.ticket.pricegroup ? ticketItem.ticket.pricegroup.Name : tr("Sélectionner un tarif");

        const price = siteSettings.taxesOnlyAtCheckout ? ticketItem.ticket.pricegroup.TotalBeforeTaxes : ticketItem.ticket.pricegroup.Total;
        rightText = ticketItem.ticket.pricegroup ? accounting.formatMoney(price) : "-";
        otherInfo = ticketItem.ticket.ticket.Name;

        handleOnLabelClick = () => { onTicketLabelClick(ticketItem._id); };
        handleOnRemoveClick = () => { onSelectedTicketsRemoved([ ticketItem.ticket.ticketId ]); };

        selectedTicketLabels.push(createSelectedTicketLabelView(item._id, mainText, otherInfo, rightText, ticketInfosLoading, handleOnLabelClick, handleOnRemoveClick, warnings));

    } else if(item.type === AddToCartItemType.Package) {
        const packageItem = item as AddToCartPackageItem;

        const packageDateItems = packageItem.packageDateItems;
        const firstTicket = packageDateItems[0].ticket;

        if(firstTicket.pricegroup) {
            mainText = firstTicket.pricegroup.Name;

            const amount = packageDateItems.reduce((acc, item) => siteSettings.taxesOnlyAtCheckout ? acc + item.ticket.pricegroup.TotalBeforeTaxes : acc + item.ticket.pricegroup.Total, 0);
            rightText = accounting.formatMoney(amount);
            otherInfo = uniq(packageDateItems.map(item => item.ticket.ticket.Name)).join(", ");
        } else {
            rightText = "-";
            mainText = tr("Sélectionner un tarif");
        }

        handleOnLabelClick = () => { onTicketLabelClick(packageItem._id); };
        handleOnRemoveClick = () => { onSelectedTicketsRemoved(getTicketIdsFromItems([packageItem])); };

        selectedTicketLabels.push(createSelectedTicketLabelView(item._id, mainText, otherInfo, rightText, ticketInfosLoading, handleOnLabelClick, handleOnRemoveClick, warnings));

    } else if(item.type === AddToCartItemType.TicketPack) {
        const ticketPackItem = item as AddToCartTicketPackItem;
        const firstTicket = ticketPackItem.tickets[0];

        otherInfo = firstTicket ? firstTicket.ticket.Name : "";
        if(ticketPackItem.tickets.length > 1)
            otherInfo = `${ticketPackItem.tickets.length} x ` + mainText;


        if(firstTicket.pricegroup) {
            const amount = ticketPackItem.tickets.reduce((acc, ticket) => acc + ticket.pricegroup.Total, 0);
            rightText = accounting.formatMoney(amount);
            mainText = firstTicket.pricegroup.Name;
        } else {
            rightText = "-";
            mainText = tr("Sélectionner un tarif");
        }

        // TODO: Faire en sorte que la modification fonctionne avec un liste d'ID pour modifier tous les billets en même temps. Je ne l'ai pas fait car le système de ticket pack n'existe pas encore officiellement.
        //handleOnLabelClick = () => { onTicketLabelClick(ticketPackItem.ticket.ticketId); };
        handleOnRemoveClick = () => { onSelectedTicketsRemoved(ticketPackItem.tickets.map(t => t.ticketId) ); };

        selectedTicketLabels.push(createSelectedTicketLabelView(item._id, mainText, otherInfo, rightText, ticketInfosLoading, handleOnLabelClick, handleOnRemoveClick, warnings));
    }

    // eslint-disable-next-line sonarjs/jsx-no-useless-fragment
    return <>{selectedTicketLabels}</>;
} 